import { useState, useEffect } from "react";
import {
  MAKES,
  findMakeBySlug,
  formatModelTitle,
  slugifyMake,
  slugifyModel,
} from "../../lib/vehicle/makes";
import { formatUkReg, isValidUkReg, normalizeUkReg } from "../../lib/vehicle/regValidation";
import { listRecent } from "../utils/recentRegs";
import SlCloudNav from "../brands/servicelad/SlCloudNav";

// ServiceLad uses its one shared header here too; the other brands keep this bar.
function MakesHeader({ brand }) {
  if (brand.key === "servicelad") return <SlCloudNav />;
  return (
    <header className="border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-20">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
        <a href="/" className="font-heading text-xl font-bold tracking-tight text-slate-900">
          {brand.name}
        </a>
        <nav className="flex gap-4 text-sm font-semibold text-slate-600">
          <a href="/vehicle-check" className="hover:text-slate-900">Vehicle check</a>
          <a href="/pricing" className="hover:text-slate-900">Pricing</a>
          <a href="/about" className="hover:text-slate-900">About</a>
        </nav>
      </div>
    </header>
  );
}

export default function MakesPage({ brand, makeSlug, modelSlug }) {
  const [vrm, setVrm] = useState("");
  const [makeData, setMakeData] = useState({ models: [], vehicles: [] });
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);

  const makeDef = makeSlug ? findMakeBySlug(makeSlug) : null;
  const canonicalMakeSlug = makeDef ? makeDef.slug : slugifyMake(makeSlug || "");
  const normalizedModelSlug = modelSlug ? slugifyModel(modelSlug) : "";
  const displayModelName = normalizedModelSlug ? formatModelTitle(normalizedModelSlug) : "";

  // Fetch live search results for this make from D1 API if viewing a specific make
  useEffect(() => {
    if (!makeDef) return;
    let cancelled = false;
    setLoading(true);

    fetch(`/api/makes/${encodeURIComponent(canonicalMakeSlug)}`)
      .then((res) => {
        if (!res.ok) throw new Error("API not available");
        return res.json();
      })
      .then((data) => {
        if (!cancelled && data) {
          setMakeData({
            models: data.models || [],
            vehicles: data.vehicles || [],
          });
        }
      })
      .catch(() => {
        // Fallback to local storage or defaults
        if (!cancelled) {
          const recentList = listRecent(brand.key, 10);
          const localCars = recentList
            .filter((entry) => {
              if (!entry.make) return false;
              const s = slugifyMake(entry.make);
              return s === canonicalMakeSlug || makeDef.dbMakes.includes(String(entry.make).toUpperCase());
            })
            .map((entry) => ({
              registration: entry.reg,
              make: entry.make,
              model: entry.model || "",
              model_year: entry.year || null,
            }));

          setMakeData({
            models: (makeDef.popularModels || []).map((m) => ({ name: m, slug: slugifyModel(m) })),
            vehicles: localCars,
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canonicalMakeSlug, makeDef, brand.key]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const clean = normalizeUkReg(vrm);
    if (!clean || checking) return;
    setChecking(true);
    window.location.href = `/vehicle/${clean.toLowerCase()}`;
  };

  // 1. All Makes Overview (/makes)
  if (!makeSlug || !makeDef) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900">
        <MakesHeader brand={brand} />

        <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
          <nav className="text-xs text-slate-500 mb-6" aria-label="Breadcrumb">
            <a href="/" className="text-blue-600 hover:underline">Home</a> / Supported Makes
          </nav>

          <div className="max-w-2xl">
            <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Supported Vehicle Manufacturers
            </h1>
            <p className="mt-2 text-base text-slate-600">
              Official digital manufacturer service records are available for major makes from model year 2012 onward. Full risk checks, MOT history and write-off markers cover all UK vehicles.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {MAKES.map((m) => (
              <a
                key={m.slug}
                href={`/makes/${m.slug}`}
                className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm transition hover:border-blue-500 hover:shadow-md"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-50 p-2 border border-slate-100">
                  <img src={`/brands/${m.file}`} alt={m.name} className="max-h-full max-w-full object-contain" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-sm text-slate-900 truncate">{m.name}</p>
                  <p className="text-[11px] text-slate-500">{m.serviceHistory ? "2012+ records" : "MOT check"}</p>
                </div>
              </a>
            ))}
          </div>
        </main>
      </div>
    );
  }

  // Filter vehicles for specific model if modelSlug is provided
  const displayedVehicles = normalizedModelSlug
    ? makeData.vehicles.filter((v) => {
        const slug = slugifyModel(v.model);
        return slug === normalizedModelSlug || slug.startsWith(`${normalizedModelSlug}-`) || normalizedModelSlug.startsWith(`${slug}-`);
      })
    : makeData.vehicles;

  const headerTitle = normalizedModelSlug
    ? `${makeDef.name} ${displayModelName}`
    : `${makeDef.name} Vehicle Checks`;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <MakesHeader brand={brand} />

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        {/* Breadcrumb Navigation */}
        <nav className="text-xs text-slate-500 mb-6" aria-label="Breadcrumb">
          <a href="/" className="text-blue-600 hover:underline">Home</a>
          {" / "}
          <a href="/makes" className="text-blue-600 hover:underline">Makes</a>
          {" / "}
          {normalizedModelSlug ? (
            <>
              <a href={`/makes/${makeDef.slug}`} className="text-blue-600 hover:underline">{makeDef.name}</a>
              {" / "}
              <span className="text-slate-700">{displayModelName}</span>
            </>
          ) : (
            <span className="text-slate-700">{makeDef.name}</span>
          )}
        </nav>

        {/* Hero Card */}
        <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 p-3 shadow-inner">
              <img src={`/brands/${makeDef.file}`} alt={makeDef.name} className="max-h-full max-w-full object-contain" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                {headerTitle}
              </h1>
              <p className="mt-1 text-sm text-slate-600 max-w-2xl">
                Free UK road tax estimate, MOT history, and digital main dealer service history verification for {makeDef.name}.
              </p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                {makeDef.serviceHistory ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-50 px-2.5 py-1 font-medium text-emerald-800">
                    From 2012 model year
                  </span>
                ) : null}
                <span className="inline-flex items-center gap-1 rounded-full border border-blue-500/30 bg-blue-50 px-2.5 py-1 font-medium text-blue-800">
                  Main dealer history trail
                </span>
                <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 font-medium text-slate-700">
                  MOT &amp; write-off checks
                </span>
              </div>
            </div>
          </div>

          {/* Registration Search Box */}
          <div className="mt-6 border-t border-slate-100 pt-6">
            <h2 className="text-sm font-semibold text-slate-800">
              Check any {normalizedModelSlug ? `${makeDef.name} ${displayModelName}` : makeDef.name} registration
            </h2>
            <form onSubmit={handleSubmit} className="mt-3 flex flex-wrap items-center gap-3">
              <div className="relative">
                <input
                  type="text"
                  value={vrm}
                  onChange={(e) => setVrm(e.target.value.toUpperCase())}
                  placeholder="ENTER REG"
                  maxLength={8}
                  className="h-12 w-48 rounded-lg border-2 border-slate-900 bg-[#f7d54a] px-3 font-mono text-xl font-black uppercase tracking-wider text-slate-950 placeholder:text-slate-700/60 focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>
              <button
                type="submit"
                disabled={checking}
                aria-busy={checking}
                className={`h-12 rounded-lg bg-slate-900 font-semibold text-sm text-white shadow hover:bg-black transition ${checking ? "w-12 px-0" : "px-6"}`}
              >
                {checking ? (
                  <span
                    className="inline-block h-[18px] w-[18px] rounded-full border-2 border-white border-r-transparent animate-spin"
                    aria-hidden="true"
                  />
                ) : (
                  "Check vehicle"
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Popular Models Grid */}
        <section className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-bold text-slate-900">
              {normalizedModelSlug ? `Other ${makeDef.name} models` : `Popular ${makeDef.name} models`}
            </h2>
            {normalizedModelSlug ? (
              <a href={`/makes/${makeDef.slug}`} className="text-xs font-semibold text-blue-600 hover:underline">
                View all {makeDef.name} models &rarr;
              </a>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {(makeData.models.length > 0 ? makeData.models : (makeDef.popularModels || []).map((m) => ({ name: m, slug: slugifyModel(m) })))
              .filter((m) => m.slug !== normalizedModelSlug)
              .slice(0, 12)
              .map((m) => (
                <a
                  key={m.slug}
                  href={`/makes/${makeDef.slug}/${m.slug}`}
                  className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3 text-sm font-medium text-slate-800 shadow-sm transition hover:border-blue-500 hover:shadow"
                >
                  <span className="truncate">{m.name}</span>
                  {m.count ? (
                    <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                      {m.count}
                    </span>
                  ) : null}
                </a>
              ))}
          </div>
        </section>

        {/* Searched Vehicles Grid */}
        <section className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-heading text-lg font-bold text-slate-900">
                {normalizedModelSlug
                  ? `Searched ${makeDef.name} ${displayModelName} vehicles`
                  : `Recently searched ${makeDef.name} vehicles`}
              </h2>
              <p className="text-xs text-slate-500">
                Every vehicle checked through {brand.name} receives a free vehicle report.
              </p>
            </div>
          </div>

          {displayedVehicles.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {displayedVehicles.map((v) => {
                const formattedReg = formatUkReg(v.registration);
                const carName = [v.model_year, v.make, v.model].filter(Boolean).join(" ") || "Vehicle";
                return (
                  <a
                    key={v.registration}
                    href={`/vehicle/${String(v.registration).toLowerCase()}`}
                    className="flex flex-col justify-between rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-blue-500 hover:shadow-md"
                  >
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="inline-flex rounded-md border border-slate-900 bg-[#f7d54a] px-2 py-1 font-mono text-xs font-bold text-slate-950">
                          {formattedReg}
                        </span>
                        {v.mot_status ? (
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                            {v.mot_status}
                          </span>
                        ) : null}
                      </div>
                      <h3 className="font-semibold text-sm text-slate-900 line-clamp-1">{carName}</h3>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {v.model_year ? (
                          <span className="rounded bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-500 border border-slate-100">
                            {v.model_year}
                          </span>
                        ) : null}
                        {v.fuel_type ? (
                          <span className="rounded bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-500 border border-slate-100">
                            {v.fuel_type}
                          </span>
                        ) : null}
                        {v.colour ? (
                          <span className="rounded bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-500 border border-slate-100">
                            {v.colour}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <span className="mt-4 inline-flex items-center text-xs font-semibold text-blue-600">
                      View report &rarr;
                    </span>
                  </a>
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center">
              <h3 className="text-sm font-semibold text-slate-900">
                Be the first to check a {normalizedModelSlug ? `${makeDef.name} ${displayModelName}` : makeDef.name}
              </h3>
              <p className="mt-1 text-xs text-slate-500 max-w-md mx-auto">
                No vehicles have been cached for this model yet. Enter your registration plate above to generate its free vehicle page.
              </p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
